/**
 * Master Tour Calendar deep links.
 * Canonical: /calendar/{page}/{date}  e.g. /calendar/guest-list/2026-09-03
 * /my-calendar stays as an alias so existing shares and bookmarks still work.
 */

export const CALENDAR_PATH = 'calendar';
export const CALENDAR_PATH_LEGACY = 'my-calendar';

export const CALENDAR_RESERVED = new Set(['share', 'day']);

/** Internal canvas id → URL slug */
export const CANVAS_SLUGS = {
  month: 'month',
  week: 'week',
  timeline: 'timeline',
  map: 'map',
  expenses: 'expenses',
  files: 'files',
  tourparty: 'tour-party',
  chat: 'chat',
  riders: 'rider',
  setlist: 'setlist',
  guestlist: 'guest-list',
  day: 'schedule',
  contacts: 'contacts',
  merch: 'merch',
  settle: 'settlement',
  lodging: 'lodging',
  daysheet: 'day-sheet',
  stage: 'stage',
  notes: 'notes',
  invite: 'invite',
};

const SLUG_TO_CANVAS = Object.fromEntries(
  Object.entries(CANVAS_SLUGS).map(([id, slug]) => [slug, id])
);

SLUG_TO_CANVAS['guestlist'] = 'guestlist';
SLUG_TO_CANVAS['guest-list'] = 'guestlist';
SLUG_TO_CANVAS['set-list'] = 'setlist';
SLUG_TO_CANVAS['setlist'] = 'setlist';
SLUG_TO_CANVAS['tourparty'] = 'tourparty';
SLUG_TO_CANVAS['tour-party'] = 'tourparty';
SLUG_TO_CANVAS['daysheet'] = 'daysheet';
SLUG_TO_CANVAS['day-sheet'] = 'daysheet';
SLUG_TO_CANVAS['riders'] = 'riders';
SLUG_TO_CANVAS['rider'] = 'riders';
SLUG_TO_CANVAS['settle'] = 'settle';
SLUG_TO_CANVAS['settlement'] = 'settle';
SLUG_TO_CANVAS['schedule'] = 'day';
SLUG_TO_CANVAS['invite'] = 'tourparty';
SLUG_TO_CANVAS['contact'] = 'contacts';
SLUG_TO_CANVAS['contacts'] = 'contacts';
/** Old /calendar/stage bookmarks open Rider instead of 404. */
SLUG_TO_CANVAS['stage'] = 'riders';

export const DATE_KEY_RE = /^\d{4}-\d{2}-\d{2}$/;

export function isCalendarDateKey(value) {
  return DATE_KEY_RE.test(String(value || '').trim());
}

export function slugFromCanvas(canvas) {
  const id = String(canvas || '').trim();
  if (!id) return 'month';
  return CANVAS_SLUGS[id] || id.replace(/_/g, '-');
}

export function canvasFromSlug(slug) {
  const raw = String(slug || '').trim().toLowerCase();
  if (!raw || CALENDAR_RESERVED.has(raw)) return null;
  return SLUG_TO_CANVAS[raw] || raw.replace(/-/g, '');
}

export function rewriteCalendarPath(path) {
  const raw = String(path || '');
  const qIndex = raw.indexOf('?');
  const pathname = qIndex >= 0 ? raw.slice(0, qIndex) : raw;
  const search = qIndex >= 0 ? raw.slice(qIndex) : '';
  let p = pathname.replace(/\/+$/, '') || '/';
  if (p === `/${CALENDAR_PATH_LEGACY}` || p.startsWith(`/${CALENDAR_PATH_LEGACY}/`)) {
    p = p.replace(new RegExp(`^/${CALENDAR_PATH_LEGACY}`), `/${CALENDAR_PATH}`);
  }
  return `${p}${search}`;
}

export function buildCalendarPath(canvas, dateKey) {
  const slug = slugFromCanvas(canvas);
  const date = isCalendarDateKey(dateKey) ? dateKey : '';
  if (!slug || slug === 'month') {
    return date ? `/${CALENDAR_PATH}/month/${date}` : `/${CALENDAR_PATH}`;
  }
  return date ? `/${CALENDAR_PATH}/${slug}/${date}` : `/${CALENDAR_PATH}/${slug}`;
}

export function buildCalendarSharePath(token) {
  return `/${CALENDAR_PATH}/share/${encodeURIComponent(String(token || '').trim())}`;
}

export function buildCalendarDayPath(dateKey) {
  return `/${CALENDAR_PATH}/day/${encodeURIComponent(String(dateKey || '').trim())}`;
}

export function parseCalendarPath(path) {
  const rewritten = rewriteCalendarPath(path);
  const pathname = rewritten.split('?')[0];
  const parts = pathname.split('/').filter(Boolean);
  if (parts[0] !== CALENDAR_PATH) return { kind: 'other' };
  if (parts[1] === 'share') return { kind: 'share', token: decodeURI(parts[2] || '') };
  if (parts[1] === 'day') return { kind: 'day', dateKey: decodeURI(parts[2] || '') };
  const a = parts[1] || '';
  const b = parts[2] || '';
  if (isCalendarDateKey(a) && !b) return { kind: 'canvas', canvas: 'month', dateKey: a };
  return {
    kind: 'canvas',
    canvas: canvasFromSlug(a) || 'month',
    dateKey: isCalendarDateKey(b) ? b : '',
  };
}
